import React, { useEffect, useRef, useState } from 'react';
import { Shield, Menu, X, Sun, Moon, Monitor, Check, Lock } from 'lucide-react';
import { navLinkClass, mobileNavItemClass, buttonClass } from '../../uiTokens';
import Button from '../ui/Button';

export const PRIMARY_PAGES = [
  { id: 'home', label: 'Share' },
  { id: 'use-cases', label: 'Use cases' },
  { id: 'cli', label: 'Install' },
  { id: 'pricing', label: 'Pricing' },
];

export const SECONDARY_PAGES = [
  { id: 'about', label: 'About' },
  { id: 'privacy', label: 'Privacy' },
];

const THEME_OPTIONS = [
  { id: 'light', label: 'Light', Icon: Sun },
  { id: 'dark', label: 'Dark', Icon: Moon },
  { id: 'auto', label: 'System', Icon: Monitor },
];

function ThemeMenu({ themeMode, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = THEME_OPTIONS.find((option) => option.id === themeMode) || THEME_OPTIONS[2];

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Theme: ${current.label}`}
        title={`Theme: ${current.label}`}
        className={buttonClass({ variant: 'quiet', size: 'icon' })}
      >
        <current.Icon className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Theme"
          className="absolute right-0 mt-2 w-40 rounded-xl bg-surface shadow-e3 p-1 z-50 animate-scale-in origin-top-right"
        >
          {THEME_OPTIONS.map(({ id, label, Icon }) => {
            const selected = themeMode === id;
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-2.5 h-10 rounded-lg text-sm transition-colors ${
                  selected ? 'text-fg bg-fg/5' : 'text-muted hover:text-fg hover:bg-fg/5'
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span className="flex-1 text-left">{label}</span>
                {selected && <Check className="h-4 w-4 text-accent" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppHeader({ currentPage, onNavigate, themeMode, setTheme }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [currentPage]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 2);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  const handleNavigate = (page) => {
    setMobileNavOpen(false);
    onNavigate(page);
  };

  const headerClass = [
    'sticky top-0 z-40 bg-canvas/85 backdrop-blur-md supports-[backdrop-filter]:bg-canvas/80 transition-shadow duration-200',
    'rule-fade-b',
    scrolled ? 'shadow-[0_1px_0_var(--line)]' : '',
  ].join(' ');

  return (
    <header className={headerClass}>
      <div className="max-w-page mx-auto safe-x flex items-center gap-8 h-14">
        <button
          type="button"
          onClick={() => handleNavigate('home')}
          className="flex items-center gap-2.5 shrink-0 rounded-md transition-opacity hover:opacity-85"
          aria-label="Hushify home"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-[7px] bg-accent text-accent-fg">
            <Shield className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
          </span>
          <span className="text-[17px] font-semibold tracking-[-0.01em] text-fg">hushify</span>
        </button>

        <nav className="hidden md:flex items-center gap-6 min-w-0" aria-label="Main">
          {PRIMARY_PAGES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleNavigate(id)}
              aria-current={currentPage === id ? 'page' : undefined}
              className={navLinkClass(currentPage === id)}
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 shrink-0">
          {currentPage !== 'home' && (
            <Button
              size="sm"
              onClick={() => handleNavigate('home')}
              className="hidden md:inline-flex mr-1"
            >
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              Share a secret
            </Button>
          )}

          <ThemeMenu themeMode={themeMode} onChange={setTheme} />

          <button
            type="button"
            className={`md:hidden ${buttonClass({ variant: 'quiet', size: 'icon' })}`}
            aria-expanded={mobileNavOpen}
            aria-controls="mobile-nav"
            aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {mobileNavOpen && (
        <nav
          id="mobile-nav"
          aria-label="Main"
          className="md:hidden safe-x pb-4 pt-2 flex flex-col gap-1 animate-rise-fast border-t border-line"
        >
          {PRIMARY_PAGES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleNavigate(id)}
              aria-current={currentPage === id ? 'page' : undefined}
              className={mobileNavItemClass(currentPage === id)}
            >
              {label}
            </button>
          ))}
          <div className="my-2 border-t border-line" role="separator" />
          {SECONDARY_PAGES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleNavigate(id)}
              aria-current={currentPage === id ? 'page' : undefined}
              className={mobileNavItemClass(currentPage === id)}
            >
              {label}
            </button>
          ))}
        </nav>
      )}
    </header>
  );
}
